import { isPreview } from "../lib/preview";
import { useEffect, useId, useRef } from "react";
import { Check, LoaderCircle, PartyPopper } from "lucide-react";
import {
  Alert,
  Button,
  EmptyState,
  Progress,
  RadioGroup,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@codelittinc/carbon-design-system";
import type { QuizResult, TrainingItem } from "@carbon-os/server/contract";
import { trackApp, trackTraining, type TrainingEvents } from "../lib/analytics";
import { allSaved, type QuizControls, type QuizView, type SaveState } from "../lib/quiz";
import { ACTION, ICON, Panel, Unavailable, type FocusRef } from "./Panels";

const FOOTER_ACTION = "h-11 flex-1 sm:h-9 sm:flex-none";

const SAVE_FAILED = "Couldn't save your answer";
const SUBMIT_FAILED = "We couldn't submit your answers. They're saved, so try again.";
const INCOMPLETE = "This one still needs an answer.";
const UNAVAILABLE = "We can't reach your quiz right now";

const score = (result: QuizResult) => `${result.correct} of ${result.total} (${result.percent}%)`;

/** Where the quiz was when it was closed, and how far the attempt had got. */
function closedAt(view: QuizView, item: TrainingItem): Pick<TrainingEvents["training_quiz_closed"], "stage" | "answered" | "total"> | null {
  switch (view.kind) {
    case "closed":
      return null;
    case "loading":
    case "unavailable":
      return { stage: view.kind, answered: item.quiz?.inProgress?.answered ?? 0, total: item.quiz?.questionCount ?? 0 };
    case "answering":
    case "submitting": {
      const { questions } = view.attempt;
      return { stage: "answering", answered: questions.filter((question) => view.saves[question.id] === "saved").length, total: questions.length };
    }
    case "result":
      return { stage: "result", answered: view.result.total, total: view.result.total };
  }
}

/**
 * What the Sheet's own live region says. The page's region sits outside the
 * modal Sheet, where a screen reader no longer listens.
 */
function announcement(view: QuizView): string {
  switch (view.kind) {
    case "loading":
      return "Loading your quiz";
    case "unavailable":
      return view.retrying ? "" : UNAVAILABLE;
    case "submitting":
      return "Submitting your answers…";
    case "result":
      return view.result.passed
        ? `You passed: ${score(view.result)}`
        : `Not this time. ${score(view.result)}. You need ${view.result.passPercent}%.`;
    case "answering": {
      if (view.notice === "submit_failed") return SUBMIT_FAILED;
      if (view.notice === "incomplete") return INCOMPLETE;
      const question = view.attempt.questions[view.index];
      const save = question ? view.saves[question.id] : undefined;
      return save === "saving" ? "Saving…" : save === "saved" ? "Saved" : save === "failed" ? SAVE_FAILED : "";
    }
    case "closed":
      return "";
  }
}

/**
 * The quiz, one question at a time, inside the watch view or a standalone Sheet.
 * Every answer is saved as it is picked, so closing
 * it at any point loses nothing.
 */
export function Quiz({ quiz, onCloseAutoFocus, inline = false }: {
  quiz: QuizControls;
  onCloseAutoFocus?: (event: Event) => void;
  inline?: boolean;
}) {
  const { view, item } = quiz;
  const focusTarget = useRef<HTMLElement | null>(null);
  // Its own Close and the sheet's; the player closing around it counts as the player's.
  const close = () => {
    const at = item ? closedAt(view, item) : null;
    if (item && quiz.trackId && at) trackTraining("training_quiz_closed", { material_id: item.materialId, track_id: quiz.trackId, ...at });
    quiz.close();
  };
  const focusRef: FocusRef = (element) => {
    focusTarget.current = element;
  };

  // Each new question, the result and the unavailable panel take focus, so
  // they are read out; a save or a submit in place does not move it.
  const step =
    view.kind === "answering" || view.kind === "submitting" ? `${view.attempt.attemptId}:${view.index}` : view.kind;
  useEffect(() => {
    focusTarget.current?.focus();
  }, [step]);

  const content = (
    <>
      <p role="status" aria-live="polite" className="sr-only">
        {announcement(view)}
      </p>
      <SheetHeader className="pr-14">
        {inline ? <h2 className="text-lg font-semibold">Questions</h2> : <SheetTitle className="line-clamp-2 break-words">{item?.title ?? "Quiz"}</SheetTitle>}
        {view.kind === "answering" || view.kind === "submitting" ? (
          <>
            <p className="mt-1 text-sm text-text-secondary">
              Question {view.index + 1} of {view.attempt.questions.length}
            </p>
            <Progress
              className="mt-2"
              value={view.attempt.questions.filter((question) => view.saves[question.id] === "saved").length}
              max={view.attempt.questions.length}
              aria-label="Quiz progress"
            />
          </>
        ) : null}
      </SheetHeader>
      {view.kind === "loading" ? <Loading /> : null}
      {view.kind === "unavailable" ? (
        <SheetBody>
          <Unavailable
            title={UNAVAILABLE}
            description="Your answers are safe."
            retrying={view.retrying}
            retry={() => { trackApp("retry_clicked", { area: "quiz" }); quiz.tryAgain(); }}
            focusRef={focusRef}
          />
        </SheetBody>
      ) : null}
      {view.kind === "answering" || view.kind === "submitting" ? (
        <Question view={view} quiz={quiz} focusRef={focusRef} />
      ) : null}
      {view.kind === "result" && item ? (
        <Result result={view.result} item={item} tryAgain={quiz.tryAgain} close={close} focusRef={focusRef} />
      ) : null}
    </>
  );

  if (inline) return <section aria-label="Video questions" className="flex min-h-96 flex-col">{content}</section>;
  return (
    <Sheet open={view.kind !== "closed"} onOpenChange={(open) => { if (!open) close(); }}>
      <SheetContent side="right" aria-describedby={undefined} onCloseAutoFocus={onCloseAutoFocus}>
        {content}
      </SheetContent>
    </Sheet>
  );
}

function Loading() {
  return (
    <SheetBody>
      <Skeleton className="h-6 w-3/4 motion-reduce:animate-none" />
      <div className="mt-4 flex flex-col gap-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
        ))}
      </div>
    </SheetBody>
  );
}

function Question({
  view,
  quiz,
  focusRef,
}: {
  view: Extract<QuizView, { kind: "answering" | "submitting" }>;
  quiz: QuizControls;
  focusRef: FocusRef;
}) {
  const promptId = useId();
  const question = view.attempt.questions[view.index];
  if (!question) return null;
  const last = view.index === view.attempt.questions.length - 1;
  const submitting = view.kind === "submitting";
  const notice = view.kind === "answering" ? view.notice : undefined;

  return (
    <>
      <SheetBody>
        <h3
          ref={focusRef}
          id={promptId}
          tabIndex={-1}
          className="text-base font-medium break-words text-text-primary outline-none"
        >
          {question.prompt}
        </h3>
        {notice === "incomplete" ? (
          <Alert variant="warning" className="mt-3">
            {INCOMPLETE}
          </Alert>
        ) : null}
        <RadioGroup
          key={question.id}
          size="touch"
          className="mt-4"
          aria-labelledby={promptId}
          value={question.answer ?? ""}
          onChange={(choiceId) => quiz.choose(question.id, choiceId)}
          options={question.choices.map((choice) => ({ value: choice.id, label: choice.text }))}
          disabled={submitting || isPreview()}
        />
        <SaveLine state={view.saves[question.id]} retry={() => { trackApp("retry_clicked", { area: "quiz_answer" }); quiz.retrySave(question.id); }} />
        {notice === "submit_failed" ? (
          <Alert variant="error" className="mt-3">
            {SUBMIT_FAILED}
          </Alert>
        ) : null}
      </SheetBody>
      <SheetFooter>
        {view.index > 0 ? (
          <Button variant="outline" className={`mr-auto ${FOOTER_ACTION}`} disabled={submitting || isPreview()} onClick={quiz.back}>
            Back
          </Button>
        ) : null}
        {!last ? (
          <Button className={FOOTER_ACTION} disabled={view.saves[question.id] !== "saved"} onClick={quiz.next}>
            Next
          </Button>
        ) : allSaved(view) ? (
          <Button className={FOOTER_ACTION} disabled={submitting || isPreview()} onClick={quiz.submit}>
            {submitting ? (
              <>
                <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
                Submitting…
              </>
            ) : (
              "Submit answers"
            )}
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

function SaveLine({ state, retry }: { state: SaveState | undefined; retry: () => void }) {
  if (state === "failed") {
    return (
      <Alert variant="error" title={SAVE_FAILED} className="mt-3">
        <Button variant="outline" className={`mt-2 ${ACTION}`} onClick={retry}>
          Try again
        </Button>
      </Alert>
    );
  }
  if (!state) return null;
  return (
    <p className="mt-3 flex items-center gap-1.5 text-sm text-text-secondary">
      {state === "saving" ? (
        <>
          <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
          Saving…
        </>
      ) : (
        <>
          <Check className="size-4" aria-hidden />
          Saved
        </>
      )}
    </p>
  );
}

function Result({
  result,
  item,
  tryAgain,
  close,
  focusRef,
}: {
  result: QuizResult;
  item: TrainingItem;
  tryAgain: () => void;
  close: () => void;
  focusRef: FocusRef;
}) {
  if (result.passed) {
    return (
      <>
        <SheetBody>
          <Panel focusRef={focusRef}>
            <EmptyState
              icon={<PartyPopper className={ICON} />}
              title={`You passed: ${score(result)}`}
              description={`${item.title} is marked done.`}
            />
          </Panel>
        </SheetBody>
        <SheetFooter>
          <Button className={FOOTER_ACTION} onClick={close}>Close</Button>
        </SheetFooter>
      </>
    );
  }
  return (
    <>
      <SheetBody>
        <h3 ref={focusRef} tabIndex={-1} className="text-lg font-semibold text-text-primary outline-none">
          Not this time
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          {score(result)}. You need {result.passPercent}%.
        </p>
        {result.missed.length > 0 ? (
          <>
            <p className="mt-4 text-sm font-medium text-text-primary">Have another look at:</p>
            <ol aria-label="Missed questions" className="mt-2 flex list-decimal flex-col gap-1 pl-5 text-sm text-text-primary">
              {result.missed.map((missed) => (
                <li key={missed.questionId}>
                  <span className="line-clamp-3 break-words">{missed.prompt}</span>
                </li>
              ))}
            </ol>
          </>
        ) : null}
        <p className="mt-4 text-sm text-text-secondary">
          Go back over the {item.kind === "video" ? "video" : "file"}, then try again.
        </p>
      </SheetBody>
      <SheetFooter>
        <Button variant="outline" className={`mr-auto ${FOOTER_ACTION}`} onClick={close}>
          Close
        </Button>
        <Button className={FOOTER_ACTION} onClick={tryAgain}>
          Try again
        </Button>
      </SheetFooter>
    </>
  );
}
